import { Grid, ListItemText, MenuItem, Paper, styled, TextField } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { DateTime, MonthNumbers } from "luxon";
import { ChangeEvent, FC, useMemo, useState } from "react";
import { FinancesCurrentFutureRow, FinancesEngloboLandProject, ProjectStatuses } from "../../api/dtos";
import SetLinks from "../../components/layout/SetLinks";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import useFinances from "../../hooks/data/useFinances";
import useProjects from "../../hooks/data/useProjects";

const getMonthString = (month: MonthNumbers) => {
    switch(month) {
        case 1:
            return "January";
        case 2:
            return "February";
        case 3:
            return "March";
        case 4:
            return "April";
        case 5:
            return "May";
        case 6:
            return "June";
        case 7:
            return "July";
        case 8:
            return "August";
        case 9:
            return "September";
        case 10:
            return "October";
        case 11:
            return "November";
        case 12:
            return "December";
    }
}

const useCurrentLotsColumns = () => {
    const currentLotsColumns = useMemo(() => {
        const columns: GridColDef<FinancesCurrentFutureRow>[] = [
            {
                field: "projectName",
                headerName: "Project Name",
                width: 250
            },
            {
                field: "stageName",
                headerName: "Stage Name",
                width: 130
            },
            {
                field: "unsold",
                headerName: "Unsold",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 100,
            },
            {
                field: "contractsIssued",
                headerName: "Contracts Issued",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 135,
            },
            {
                field: "contractsExchanged",
                headerName: "Contracts Exchanged",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 160,
            },
            {
                field: "unsoldIssuedValue",
                headerName: "Value of Unsold Lots & Contracts Issued",
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                minWidth: 300,
                flex: 1,
            },
            {
                field: "exchangedValue",
                headerName: "Sales Value of Exchanged Lots",
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                width: 250,
            },
            {
                field: "month1",
                headerName: getMonthString(DateTime.now().month),
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                width: 110
            },
            {
                field: "month2",
                headerName: getMonthString(DateTime.now().plus({months: 1}).month),
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                width: 110
            },
            {
                field: "month3",
                headerName: getMonthString(DateTime.now().plus({months: 2}).month),
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                width: 110
            },
            {
                field: "month4",
                headerName: getMonthString(DateTime.now().plus({months: 3}).month),
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                width: 110
            },
        ]

        return columns;
    }, [])

    return currentLotsColumns;
}

const useEngloboLandProjectsColumns = () => {
    const engloboLandProjectsColumns = useMemo(() => {
        const columns: GridColDef<FinancesEngloboLandProject>[] = [
            {
                field: "projectName",
                headerName: "Project Name",
                flex: 1
            },
            {
                field: "stageName",
                headerName: "Stage Name",
                flex: 1
            },
            {
                field: "unsold",
                headerName: "Unsold",
                type: "number",
                flex: 1
            },
            {
                field: "assetValue",
                headerName: "Asset Value",
                type: "number",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            }
        ]

        return columns;
    }, []);

    return engloboLandProjectsColumns;
}

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}))

interface IFinanceProps {

}

const Finances: FC<IFinanceProps> = () => {
    const currentLotsColumns = useCurrentLotsColumns();
    const engloboLandProjectsColumns = useEngloboLandProjectsColumns();

    const {data: projects} = useProjects();

    const [selectedProjectId, setSelectedProjectId] = useState(-1); 
    const [selectedProjectStatus, setSelectedProjectStatus] = useState<ProjectStatuses | -1>(-1);

    const {data: finances} = useFinances(selectedProjectId, selectedProjectStatus != -1 ? selectedProjectStatus : undefined);

    const handleProjectChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSelectedProjectId(Number(event.target.value));
    }

    const handleProjectStatusChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSelectedProjectStatus(Number(event.target.value) as ProjectStatuses);
    }
    
    const currentLotsCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `${projects?.find(project => project.id == selectedProjectId)?.name ??  (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Current Lot Finances`,
        exportTitle: `${projects?.find(project => project.id == selectedProjectId)?.name ??  (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Current Lot Finances`,
        includeDateTime: true
    }), [selectedProjectId]);
    
    const futureLotsCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `${projects?.find(project => project.id == selectedProjectId)?.name ??  (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Future Lot Finances`,
        exportTitle: `${projects?.find(project => project.id == selectedProjectId)?.name ??  (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Future Lot Finances`,
        includeDateTime: true
    }), [selectedProjectId]);
    
    const engloboLandProjectsCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `${projects?.find(project => project.id == selectedProjectId)?.name ?? (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Current Lot Finances`,
        exportTitle: `${projects?.find(project => project.id == selectedProjectId)?.name ?? (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Englobo Land Project Finances`,
        includeDateTime: true
    }), [selectedProjectId]);
    
    return (
        <>
            <SetLinks
                stageLinkEnabled={false}
                lotLinkEnabled={false}
            />
            <StyledPaper
                sx={{
                    padding: 2
                }}
            >
                <Grid
                    container
                    gap={2}
                >
                    <TextField
                        select
                        size="small"
                        label="Project"
                        value={selectedProjectId}
                        onChange={handleProjectChange}
                        slotProps={{
                            select: {
                                MenuProps: {
                                    sx: {
                                        maxHeight: 500
                                    }
                                }
                            }
                        }}
                        sx={{
                            width: 300
                        }}
                    >
                        <MenuItem key={-1} value={-1}><ListItemText primary={"All"} /></MenuItem>
                        {
                            projects?.map(project => 
                                <MenuItem key={project.id} value={project.id}><ListItemText primary={project.name} /></MenuItem>
                            )
                        }
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Project Status"
                        value={selectedProjectStatus}
                        onChange={handleProjectStatusChange}
                        sx={{
                            width: 200
                        }}
                    >
                        <MenuItem key={-1} value={-1}><ListItemText primary={"All"} /></MenuItem>
                        <MenuItem key={ProjectStatuses.ReleasedToMarket} value={ProjectStatuses.ReleasedToMarket}><ListItemText primary={"Released To Market"} /></MenuItem>
                        <MenuItem key={ProjectStatuses.Pipeline} value={ProjectStatuses.Pipeline}><ListItemText primary={"Pipeline"} /></MenuItem>
                        <MenuItem key={ProjectStatuses.Archived} value={ProjectStatuses.Archived}><ListItemText primary={"Archived"} /></MenuItem>
                    </TextField>
                </Grid>
            </StyledPaper>
            <StyledPaper>
                <CollapsibleTablePremium 
                    title={"Current Lots"} 
                    density="compact"
                    loading={false} 
                    columns={currentLotsColumns}
                    rows={finances?.currentLots}
                    startCollapsed
                    disableColumnMenu={false}
                    disableColumnFilter={false}
                    {...currentLotsCustomToolbarProps}
                />
            </StyledPaper>
            <StyledPaper>
                <CollapsibleTablePremium 
                    title={"Future Lots"} 
                    density="compact"
                    loading={false} 
                    columns={currentLotsColumns}
                    rows={finances?.futureLots}
                    startCollapsed
                    disableColumnMenu={false}
                    disableColumnFilter={false}
                    {...futureLotsCustomToolbarProps}
                />
            </StyledPaper>
            <StyledPaper>
                <CollapsibleTablePremium 
                    title={"Englobo Land Projects"} 
                    density="compact"
                    loading={false} 
                    columns={engloboLandProjectsColumns}
                    rows={finances?.engloboLandProjects}
                    startCollapsed
                    disableColumnMenu={false}
                    disableColumnFilter={false}
                    {...engloboLandProjectsCustomToolbarProps}
                />
            </StyledPaper>
        </>
    );
}

export default Finances;